import {PlayerBase} from './player.base';
import {ChangeDetectionStrategy, Component, ComponentFactoryResolver, Injector, ViewChild, ViewContainerRef} from '@angular/core';
import {IPlayer} from '../interfaces/IPlayer';
import {SceneComponent} from "../components/scene/scene.component";
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {CachingService} from "../services/caching/caching.service";
import {Log, LogTag} from "../services/Log";
import {catchError, finalize, tap} from "rxjs/operators";
import {IPlayerContainer} from "../interfaces/IPlayerContainer";





@Component({
  selector: 'app-player-container',
  template: '',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlayerContainer extends PlayerBase implements IPlayerContainer {
  resolver: ComponentFactoryResolver;
  childComponent: PlayerBase;
  @ViewChild('playercontainer', { read: ViewContainerRef, static: true })
  entry: ViewContainerRef;

  url;
  childProviderData: any;
  playVideoInFull = false;
  volume: number = 1.0;
  displayMode = 'slideShow';

  constructor(protected  injector: Injector) {
    super(injector);
    this.resolver = injector.get(ComponentFactoryResolver)
  }

  public getItemUrl(resourceUrl: string): string {
    if (resourceUrl!=null && resourceUrl.length>5 && resourceUrl.substring(0, 4)=='http') {
      return resourceUrl;
    }

    if (this.url==null) {
      return null;
    }

    if (resourceUrl.substring(0, 1)=='/') {
      const i = (<string>this.url).indexOf('/', 8);
      if (i>-1) {
        return this.url.substring(0, i) + resourceUrl;
      }
    } else {
      const i = (<string>this.url).lastIndexOf('/');
      if (i>-1) {
        return this.url.substring(0, i+1) + resourceUrl;
      }
    }
  }


  getPage(): Observable<any> {
    return of(null);
  }

  showPage(): Observable<any> {
    const logInfo = Log.getLogInfo("PlayerContainer", "showPage", []);
    this.loadError = false;

    const duration = Number(((new Date().getTime() - this.statStart) / 1000).toFixed(2));
    this.statStart = new Date().getTime();

    const obs$ = this.getPage();
    return obs$.pipe(
      switchMap( player => {
        if (player==null) {
          return of(null);
        }
        if (player.$.src != null) {
          const sceneId = player.$.src;
          if (sceneId==-1 || sceneId=="") {
            Log.w(logInfo, LogTag.PLAYER, "sceneId is not set");
            return of(null);
          }
          return (<CachingService>this.playerService.caching).getRecord("scenes", sceneId).pipe(
            switchMap( scene => {
              if (scene==null) {
                Log.w(logInfo, LogTag.PLAYER,'assignment do sceneId=' + sceneId + ' does not exist' );
                this.showMsg(this.playerObject.$.label, "The assigned scene was deleted");
                this.loadError = true;
                return of(null);
              }


              const retList = [];
              let oldChildComponent = null;
              if (this.childComponent != null) {
                oldChildComponent = this.childComponent;

                const oldLoader$ = of(this.childComponent).pipe(
                  tap( child => this.unloadComplete(child, duration)),
                  switchMap( child => child.unload()),
                  finalize(() => {
                    if (oldChildComponent.finish$!=null) {
                      oldChildComponent.finish$.next("finish");
                      oldChildComponent.finish$.complete();
                      oldChildComponent.finish$ = null;
                    }
                    oldChildComponent.destroy();
                    this.removeComponent(oldChildComponent);
                  })
                )
                retList.push(oldLoader$);
              }

              const player2 = JSON.parse(JSON.stringify(scene.json.Player));
              this.childComponent = this.createComponent(SceneComponent, player2, this.editMode, -1);
              (<SceneComponent>this.childComponent).childProviderData = this.childProviderData;

              (<SceneComponent> this.childComponent).originalSize = player2.Data[0].Layout[0].$;
              player2.Data[0].Layout = this.data.Layout;
              this.childComponent.usePlayerLayoutX = false;
              this.childComponent.usePlayerLayoutY = false;
              const newLoad$ = this.childComponent.load(player2);
              retList.push(newLoad$);

              return forkJoin(retList);
            })
          )
        } else {
          const playerId = player.$.player;
          if (playerId=='0') {
            this.loadError = true;
            console.error(this, "playerId=='0'");
            return of(null);
          }

          const retList = [];
          let oldChildComponent = null;

          if (this.childComponent != null) {
            oldChildComponent = this.childComponent;

            let oldLoader$ = of(this.childComponent).pipe(
              tap( child => {
                this.unloadComplete(child, duration);
              }),
              switchMap( child => child.unload()),
              finalize(() => {
                oldChildComponent.destroy();
                this.removeComponent(oldChildComponent);
              })
            )
            retList.push(oldLoader$);
          }
          let pixiClass: any = this.playerService.getComponentClasses(playerId);
          const blockingTime = this.playerService.getComponentBlockingTime(playerId);
          this.childComponent = this.createComponent(pixiClass, player, this.editMode, blockingTime);
          this.childComponent.display(false);
          const newLoad$ = this.childComponent.load(player).pipe(
            switchMap( child => {
              return child.play(false)
            })
          )
          retList.push(newLoad$);
          return forkJoin(retList)
        }
      }),
      catchError(err => {
        Log.debug();
        console.error(err);
        return of(null);
      })
    )
  }

  unloadComplete(child, duration): Observable<any> {
    if (this.playerService.stats!=null && this.statEnabled) {
      if (this.displayMode=='manual' && this.minThreshold!=null && this.maxThreshold!=null) {
        if (duration>=this.minThreshold) {
          child.addContent(this.statTag, this.statFiled, Math.min(duration, this.maxThreshold));
        }
      } else {
        if (this.displayMode=='slideShow') {
          child.addContent(this.statTag, this.statFiled, duration);
        }
      }
    }
    return of(child);
  }

  display(show) {
  }

  loadAnimation() {
  }


  unload(): Observable<any> {
    const self = this;
    if (this.childComponent==null) {
      return of(self);
    }
    return this.childComponent.unload();
  }

  createComponent(cmpClass: any, playerObject, editMode: boolean, blockingTime: number): PlayerBase {
    const logInfo = Log.getLogInfo("PlayerContainer", "createComponent", []);
    let childComponent: PlayerBase;
    const useComponent = cmpClass.prototype.constructor.useComponent; // only false in case of RSS
    let gpu = false; // if it's run in studio it will always use html.
    if (this.editMode==false) {
      if (playerObject?.$?.gpu != null) {
        gpu = playerObject.$.gpu == '1';
      } else {
        gpu = cmpClass.prototype.constructor.defaultGpu;
      }
    }
    if (gpu) {
      Log.d(logInfo, LogTag.PIXI, 'create pure class: ' + cmpClass.name);
      childComponent = new cmpClass(this.injector, this.resolver);
    } else {
      Log.d(logInfo, LogTag.PIXI, 'create Angular class: ' + cmpClass.name);
      const factory = this.resolver.resolveComponentFactory(cmpClass);
      const componentRef = this.entry.createComponent(factory);
      childComponent = <PlayerBase> componentRef.instance;
      childComponent.hostView = componentRef.hostView;
    }
    childComponent.init(this, editMode, blockingTime);
    return childComponent;
  }

  removeComponent(player: IPlayer) {
    if (this.entry!=null) {
      const i = this.entry.indexOf((<PlayerBase>player).hostView)
      if (i != -1) {
        this.entry.remove(i);
        this.changeDetectorRef?.detectChanges();
      }
    }
  }

  destroy(): void {
    if (this.childComponent!=null) {
      console.warn("Container.destroy() childComponent ")
      this.childComponent.destroy();
      this.removeComponent(this.childComponent);
      this.childComponent = null;
    }
    this.resolver = null;
    this.entry = null;
    super.destroy();
  }
}
